import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Download, ArrowRight } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const CATEGORIES = ["all", "MSME", "Women", "SC_ST", "Youth", "Agri", "General"];

export default function Schemes() {
    const { t, lang } = useLang();
    const [schemes, setSchemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("all");

    useEffect(() => {
        setLoading(true);
        apiClient
            .get("/schemes")
            .then((r) => setSchemes(r.data))
            .catch(() => setSchemes([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let list = schemes;
        if (category !== "all") list = list.filter((s) => s.category === category);
        if (q.trim()) {
            const needle = q.toLowerCase();
            list = list.filter(
                (s) =>
                    (s.name_en || "").toLowerCase().includes(needle) ||
                    (s.name_ta || "").toLowerCase().includes(needle) ||
                    (s.short_desc_en || "").toLowerCase().includes(needle),
            );
        }
        return list;
    }, [schemes, q, category]);

    return (
        <div data-testid="schemes-page">
            <section className="bg-[#1A365D] text-white pt-20 pb-16 md:pt-28 md:pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="small-caps text-[#F5B76E] mb-4">
                        {t.schemes.eyebrow}
                    </div>
                    <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl mb-6">
                        {t.schemes.title}
                    </h1>
                    <p className="max-w-3xl text-gray-300 leading-relaxed">
                        {t.schemes.subtitle}
                    </p>
                </div>
            </section>

            {/* Filters */}
            <section className="border-b border-gray-200 bg-white sticky top-20 z-30 backdrop-blur-xl bg-white/95">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="relative flex-1 max-w-xl">
                        <Search
                            size={18}
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                            data-testid="scheme-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={t.schemes.searchPlaceholder}
                            className="pl-11 h-12 border-gray-300 focus-visible:ring-[#1A365D]"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCategory(c)}
                                data-testid={`filter-${c}`}
                                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-semibold whitespace-nowrap border transition-colors ${
                                    category === c
                                        ? "bg-[#1A365D] text-white border-[#1A365D]"
                                        : "bg-white text-gray-600 border-gray-300 hover:border-[#1A365D]"
                                }`}
                            >
                                {c === "all"
                                    ? t.schemes.filterAll
                                    : t.schemes.categories[c] || c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Cards grid */}
            <section className="py-12 md:py-20 bg-[#F7FAFC] min-h-[40vh]">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    {loading ? (
                        <div className="text-center text-gray-500 py-16" data-testid="schemes-loading">
                            Loading…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div
                            className="text-center text-gray-500 py-16 border border-dashed border-gray-300 rounded-lg"
                            data-testid="schemes-empty"
                        >
                            {t.schemes.noResults}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filtered.map((s) => (
                                <SchemeCard key={s.id} scheme={s} lang={lang} t={t} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

function SchemeCard({ scheme, lang, t }) {
    return (
        <div
            className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 hover:border-[#1A365D] hover:shadow-md transition-all flex flex-col"
            data-testid={`scheme-card-${scheme.id}`}
        >
            <div className="flex items-center justify-between mb-4">
                <span className="small-caps text-[#DD6B20]">
                    {t.schemes.categories[scheme.category] || scheme.category}
                </span>
                {scheme.is_featured && (
                    <Badge className="bg-[#1A365D] hover:bg-[#1A365D] text-white text-[10px] uppercase tracking-widest">
                        {t.schemes.featured}
                    </Badge>
                )}
            </div>
            <h3 className="font-serif font-semibold text-lg text-[#1A365D] leading-snug mb-3 min-h-[64px]">
                {lang === "en" ? scheme.name_en : scheme.name_ta}
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                {lang === "en" ? scheme.short_desc_en : scheme.short_desc_ta}
            </p>
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-6">
                Subsidy · <span className="text-[#1A365D] font-semibold">{scheme.subsidy}</span>
            </div>
            <div className="flex flex-wrap gap-3 mt-auto">
                <Link
                    to={`/schemes/${scheme.id}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A365D] hover:text-[#DD6B20]"
                    data-testid={`scheme-details-${scheme.id}`}
                >
                    {t.schemes.viewDetails} <ArrowRight size={14} />
                </Link>
                {scheme.pdf_url && (
                    <a
                        href={scheme.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-gray-500 hover:text-[#DD6B20]"
                        data-testid={`scheme-pdf-${scheme.id}`}
                    >
                        <Download size={14} /> PDF
                    </a>
                )}
            </div>
        </div>
    );
}
